import {
  getColPrettyNameFromName,
  getColumnNoun,
  isInvalidAnalyticsDimension,
  guessColFromUnknownName,
  DimensionColNames,
  getGranularityConflicts,
  getSelectionLevelErrors
} from '@blisspointmedia/bpm-types/dist/Columns';
import { isKnownMetric, MASTER_METRIC_MAP } from '@blisspointmedia/bpm-types/dist/Metrics';
import { refactorEnabled } from '@blisspointmedia/bpm-types/dist/ScpChannels';
import { FilterPaneBasicState } from '@blisspointmedia/bpm-types/dist/FilterPane';
import { conditionallyPluralize } from '@src/utils/text-utils';

export type MoveColumn = (sourceId: string, destinationId: string) => void;

export type DeleteColumn = (i: number) => Promise<void>;

export const COLUMN_DND_TYPE = 'COLUMN_DND_TYPE' as const;
export const DIMENSION_COLUMN_DND_TYPE = 'DIMENSION_COLUMN_DND_TYPE' as const;

export interface HeaderInfo {
  end: number;
  start: number;
  superHeaderIndex: number;
  text: string;
  width: number;
}

export type invalidAnalyticsDetails = [boolean, string[]];

export const getInvalidAnalyticsDetails = (
  // intentionally typed as any because it will be called where typed as any

  preset: any,
  analyticsIsSource: boolean,
  experimentalAnalyticsCols: Set<DimensionColNames>,
  presetType = ''
): invalidAnalyticsDetails => {
  const invalidAnalyticDimPrettyNames: string[] = [];
  let hasInvalidAnalyicsDims = false;

  if (preset && analyticsIsSource) {
    switch (presetType) {
      case 'table': {
        if (preset.dimensionsByName) {
          hasInvalidAnalyicsDims = preset.dimensionsByName.reduce((anyInvalid, dim) => {
            const invalid = isInvalidAnalyticsDimension(dim, experimentalAnalyticsCols);
            if (invalid) {
              const prettyName = getColPrettyNameFromName(dim);
              invalidAnalyticDimPrettyNames.push(`'${prettyName}'`);
            }
            return anyInvalid || invalid;
          }, false);
        } else if (preset.dimensionColumns) {
          hasInvalidAnalyicsDims = preset.dimensionColumns.reduce((anyInvalid, dim) => {
            const dimName = dim.dimensionVarName || '';
            const invalid = isInvalidAnalyticsDimension(dimName, experimentalAnalyticsCols);
            if (invalid) {
              const prettyName = dim.dimensionTypeName || getColPrettyNameFromName(dim);
              invalidAnalyticDimPrettyNames.push(`'${prettyName}'`);
            }
            return anyInvalid || invalid;
          }, false);
        }
        break;
      }
      case 'filter': {
        // if its a preset itll be this, not not the filter state is just wahts passed in
        const filterState = preset.filter_state ? preset.filter_state : preset;
        const filterTokens = preset.filter_tokens;

        if (filterState && filterState.isAdvanced && filterState.advanced && filterTokens) {
          // this is somewhat guessing we need a refactor of advanced
          const categoryTokens = preset.filter_tokens.filter(t => t.type === 'cat');
          for (const token of categoryTokens) {
            const dimName = token.value;
            const itemInvalid = isInvalidAnalyticsDimension(dimName, experimentalAnalyticsCols);
            if (itemInvalid) {
              hasInvalidAnalyicsDims = true;
              const prettyName = getColPrettyNameFromName(dimName);
              invalidAnalyticDimPrettyNames.push(`'${prettyName}'`);
            }
          }
        } else if (filterState && filterState.isAdvanced && filterState.advanced) {
          // This is the case where an advanced filter is an unsaved current state
          // we can guess here but it's challenging and not reliable
          const advancedStr = filterState.advanced;
          const itemArray = advancedStr.split(' ');
          itemArray.forEach(str => {
            const foundDim = guessColFromUnknownName(str);
            if (foundDim) {
              const itemInvalid = isInvalidAnalyticsDimension(foundDim, experimentalAnalyticsCols);
              if (itemInvalid) {
                hasInvalidAnalyicsDims = true;
                const prettyName = getColPrettyNameFromName(foundDim);
                invalidAnalyticDimPrettyNames.push(`'${prettyName}'`);
              }
            }
          });
        } else if (filterState && filterState.basic) {
          const { selectedMap } = filterState.basic;
          if (selectedMap) {
            for (const dim of getSelectedFilterDims(selectedMap)) {
              const itemInvalid = isInvalidAnalyticsDimension(dim, experimentalAnalyticsCols);
              if (itemInvalid) {
                hasInvalidAnalyicsDims = true;
                const prettyName = getColPrettyNameFromName(dim);
                invalidAnalyticDimPrettyNames.push(`'${prettyName}'`);
              }
            }
          }
        }
        break;
      }
      case 'page': {
        // A page preset will always load the associated filter / table, no need to validate
        break;
      }
      default:
        break;
    }
  }

  return [hasInvalidAnalyicsDims, invalidAnalyticDimPrettyNames];
};

/*
 * Keys of this type are column name that are currently invalid, the string array contains prettys name
 * of the dimensions cause the key to be invalid
 */
export type InvalidGranularityDetails = Record<string, string[]>;

// getColPrettyNameFromName only knows columns, so metric blockers (e.g. Avg. Frequency) would show raw.
const getBlockerPrettyName = (name: string): string =>
  Object.hasOwn(MASTER_METRIC_MAP, name) && isKnownMetric(name)
    ? MASTER_METRIC_MAP[name].prettyName
    : getColPrettyNameFromName(name);

/*
 * Certain dimensions for the same channel are part of different granularity breakdowns and are not compatible.
 * For example in commerce, "retailer" and "keyword" dimension cannot be combined
 * So if for example the activeDims of say a filter are "platform" and "retailer", some of the available dimOptions
 * for the metrics table such as "keyword" are incompatible
 */
// option -> selected cols that make it unavailable. Decided over the whole selection in one set-based
// call; blame is every col that conflicts with the option on its own, else the core's culprits.
const getGranularityBlockers = (
  selectedCols: string[],
  options: string[],
  channel: string
): Record<string, string[]> => {
  const blockers: Record<string, string[]> = {};
  if (!refactorEnabled(channel) || selectedCols.length === 0) {
    return blockers;
  }
  const conflicts = getGranularityConflicts(channel, selectedCols, options);
  Object.entries(conflicts).forEach(([option, culprits]) => {
    const pairwise = selectedCols.filter(col =>
      Boolean(getGranularityConflicts(channel, [col], [option])[option])
    );
    blockers[option] =
      pairwise.length > 0
        ? pairwise
        : selectedCols.filter(col => culprits.includes(getBlockerPrettyName(col)));
  });
  return blockers;
};

export const getInvalidGranularityDetails = (
  activeDims: string[],
  dimOptions: string[],
  channel: string
): InvalidGranularityDetails => {
  const invalidOptions: InvalidGranularityDetails = {};
  Object.entries(getGranularityBlockers(activeDims, dimOptions, channel)).forEach(
    ([option, cols]) => {
      invalidOptions[option] = cols.map(getBlockerPrettyName);
    }
  );
  return invalidOptions;
};

export interface InvalidGranularityDetailsBySource {
  filter: InvalidGranularityDetails;
  table: InvalidGranularityDetails;
}

// Decides over filter cols + table cols together, then splits each option's blockers by source so
// tooltips keep their filter / metrics table labels. A col in both counts once, as a filter blocker.
export const getInvalidGranularityDetailsBySource = (
  filterCols: string[],
  tableCols: string[],
  options: string[],
  channel: string
): InvalidGranularityDetailsBySource => {
  const filterColSet = new Set(filterCols);
  const selectedCols = [...filterCols, ...tableCols.filter(col => !filterColSet.has(col))];
  const result: InvalidGranularityDetailsBySource = { filter: {}, table: {} };
  Object.entries(getGranularityBlockers(selectedCols, options, channel)).forEach(
    ([option, cols]) => {
      cols.forEach(col => {
        const details = filterColSet.has(col) ? result.filter : result.table;
        details[option] = [...(details[option] ?? []), getBlockerPrettyName(col)];
      });
    }
  );
  return result;
};

// Unchecking every option leaves the category key behind with all-false values, which filters nothing
export const getSelectedFilterDims = (selectedMap: FilterPaneBasicState['selectedMap']): string[] =>
  Object.entries(selectedMap)
    .filter(([, options]) => Object.values(options ?? {}).some(Boolean))
    .map(([dim]) => dim);

export const extractDimsFromFilterPreset = (preset: any): string[] => {
  const filterState = preset.filter_state ? preset.filter_state : preset;
  const filterTokens = preset.filter_tokens;
  const dims: string[] = [];

  if (filterState && filterState.isAdvanced && filterState.advanced && filterTokens) {
    // this is somewhat guessing we need a refactor of advanced
    const categoryTokens = filterTokens.filter((t: any) => t.type === 'cat');
    for (const token of categoryTokens) {
      dims.push(token.value);
    }
  } else if (filterState && filterState.isAdvanced && filterState.advanced) {
    // This is the case where an advanced filter is an unsaved current state
    // we can guess here but it's challenging and not reliable
    const advancedStr = filterState.advanced;
    const itemArray = advancedStr.split(' ');
    itemArray.forEach((str: string) => {
      const foundDim = guessColFromUnknownName(str);
      if (foundDim) {
        dims.push(foundDim);
      }
    });
  } else if (filterState && filterState.basic) {
    const { selectedMap } = filterState.basic;
    if (selectedMap) {
      dims.push(...getSelectedFilterDims(selectedMap));
    }
  }
  return dims;
};

export const getInvalidGranularityDetailsFromPreset = (
  // intentionally typed as any because it will be called where typed as any

  preset: any,
  dimOptions: string[],
  channel: string
): InvalidGranularityDetails => {
  if (!preset) {
    return getInvalidGranularityDetails([], dimOptions, channel);
  }
  const activeDims = extractDimsFromFilterPreset(preset);
  return getInvalidGranularityDetails(activeDims, dimOptions, channel);
};

// e.g. "metrics table dimension: 'Creative' and metrics table metric: 'Avg. Frequency'". Anything that
// isn't a known metric keeps reading as a dimension.
const describeCols = (prettyNames: string[], label: string): string => {
  const metrics = prettyNames.filter(name => getColumnNoun(name) === 'metric');
  const dimensions = prettyNames.filter(name => !metrics.includes(name));
  const groups: [string, string[]][] = [
    ['dimension', dimensions],
    ['metric', metrics]
  ];
  return groups
    .filter(([, names]) => names.length > 0)
    .map(
      ([noun, names]) =>
        `${label}${conditionallyPluralize(names.length, noun)}: ${names.map(name => `'${name}'`).join(', ')}`
    )
    .join(' and ');
};

interface InvalidDimMsgParams {
  prettyDims: string[] | undefined;
  isInvalidAnalyticsDim: boolean;
  conflictingTableDims?: string[];
  conflictingFilterDims?: string[];
  conflictingDeliveryDims?: string[];
  type?: 'filter' | 'table' | string;
}

export const makeInvalidDimMsg = ({
  prettyDims,
  isInvalidAnalyticsDim,
  conflictingTableDims = [],
  conflictingFilterDims = [],
  conflictingDeliveryDims = [],
  type = 'table'
}: InvalidDimMsgParams): string => {
  if (!prettyDims || prettyDims.length === 0) {
    return '';
  }

  const sources = [
    describeCols(conflictingFilterDims, 'filter '),
    describeCols(conflictingTableDims, type === 'filter' ? 'metrics table ' : ''),
    describeCols(conflictingDeliveryDims, 'delivery ')
  ].filter(Boolean);

  const granularityMessage = sources.join(' and ');
  const prettyDimsDescription = describeCols(prettyDims, '');
  const messageBeginning = `${prettyDimsDescription.charAt(0).toUpperCase()}${prettyDimsDescription.slice(1)} ${
    prettyDims.length > 1 ? 'are' : 'is'
  }`;

  return `${messageBeginning} incompatible with ${
    isInvalidAnalyticsDim ? 'SoT' : granularityMessage
  }`;
};

// A table preset's dims + data cols (data cols can be restricted too, e.g. frequency metrics), or a
// filter preset's dims
const getPresetCols = (
  // intentionally typed as any because it will be called where typed as any

  preset: any,
  presetType: 'filter' | 'table' | string
): string[] => {
  switch (presetType) {
    case 'table':
      if (preset.dimensionsByName) {
        return [...preset.dimensionsByName, ...(preset.dataColsByName ?? [])];
      }
      if (preset.dimensionColumns) {
        return [
          ...preset.dimensionColumns.map(dimInfo => dimInfo.dimensionVarName || ''),
          ...(preset.dataColumns ?? []).map(col => col.dataVarName)
        ];
      }
      return [];
    case 'filter':
      return extractDimsFromFilterPreset(preset);
    default:
      return [];
  }
};

export const getInvalidGranularityDetailsFromComparingPresets = (
  // intentionally typed as any because it will be called where typed as any

  targetPreset: any,
  invalidDetailsFromOtherPrest: InvalidGranularityDetails,
  targetPresetType: 'filter' | 'table' | string = ''
): [string[] | undefined, string[] | undefined] => {
  if (!targetPreset) {
    return [undefined, undefined];
  }
  const invalidDims = new Set<string>();
  const activeDims = new Set<string>();
  getPresetCols(targetPreset, targetPresetType).forEach(col => {
    if (invalidDetailsFromOtherPrest[col]) {
      invalidDims.add(getBlockerPrettyName(col));
      invalidDetailsFromOtherPrest[col].forEach(activeDim => activeDims.add(activeDim));
    }
  });
  if (activeDims.size > 0 && invalidDims.size > 0) {
    return [Array.from(invalidDims), Array.from(activeDims)];
  }
  return [undefined, undefined];
};

// Like getInvalidGranularityDetailsFromComparingPresets, but each preset col is checked against the
// other side's cols together with the rest of the preset, so a conflict that needs cols from both sides
// (filter Creative + a table preset with Device and Avg. Frequency) disables the preset.
export const getInvalidGranularityDetailsFromPresetAndCols = (
  // intentionally typed as any because it will be called where typed as any

  targetPreset: any,
  otherCols: string[],
  channel: string,
  targetPresetType: 'filter' | 'table' | string = ''
): [string[] | undefined, string[] | undefined] => {
  if (!targetPreset || otherCols.length === 0 || !refactorEnabled(channel)) {
    return [undefined, undefined];
  }
  const isValidSelection = (cols: string[]): boolean => {
    const errors = getSelectionLevelErrors(channel, cols);
    return !errors.granularity && !errors.frequency;
  };
  const presetCols = getPresetCols(targetPreset, targetPresetType);
  // per preset col: other-side blockers (a col that only conflicts with its own preset has none)
  const candidates = presetCols.flatMap((col, i) => {
    const restOfPreset = presetCols.filter((_, j) => j !== i);
    const blockers = getInvalidGranularityDetailsBySource(otherCols, restOfPreset, [col], channel)
      .filter[col];
    if (!blockers) {
      return [];
    }
    // A combination-only conflict names this col only if everything else fits together;
    // otherwise another col is the real conflict.
    const isSingledOut =
      otherCols.some(other => Boolean(getGranularityConflicts(channel, [other], [col])[col])) ||
      isValidSelection([...otherCols, ...restOfPreset]);
    return [{ col, blockers, isSingledOut }];
  });
  const singledOut = candidates.filter(candidate => candidate.isSingledOut);
  // No single col to blame (e.g. filter Creative + Device vs a preset with two frequency metrics):
  // the preset is still unusable when both sides are valid alone but not together. Blame the cols that
  // conflict with the other side on their own, else every blocked col.
  const isFallback =
    singledOut.length === 0 &&
    isValidSelection(otherCols) &&
    isValidSelection(presetCols) &&
    !isValidSelection([...otherCols, ...presetCols]);
  const conflictsWithOtherSide = candidates.filter(
    ({ col }) => !isValidSelection([...otherCols, col])
  );
  const fallback = conflictsWithOtherSide.length > 0 ? conflictsWithOtherSide : candidates;
  const flagged = isFallback ? fallback : singledOut;
  if (flagged.length === 0) {
    return [undefined, undefined];
  }
  return [
    Array.from(new Set(flagged.map(({ col }) => getBlockerPrettyName(col)))),
    Array.from(new Set(flagged.flatMap(({ blockers }) => blockers)))
  ];
};
